import _                                                                      from 'underscore';
import React, { FC }                                                          from 'react';
import classNames                                                             from 'classnames';
import { ClassValue }                                                         from 'classnames/types';
import { AsTagProp, BorderClassSides, CorvalColor, CorvalColorTheme, ZIndex } from '../../types';
import { Align, Position }                                                    from '../../types';
import { BorderClassSize, MarginSize, OutlineClassSize, PaddingSize }         from '../../types';
import { T_OR_NULL }                                                          from '../../types';

type DescriptiveDimensionArray<T> =
	T_OR_NULL<T>
	| [ vertical: T_OR_NULL<T>, horizontal: T_OR_NULL<T> ]
	| [ top: T_OR_NULL<T>, right: T_OR_NULL<T>, bottom: T_OR_NULL<T>, left: T_OR_NULL<T> ];

export interface StyledContainerProps {
	textAlign?: Align,
	textColor?: CorvalColor,
	bgColor?: CorvalColor,
	border?: BorderClassSize | DescriptiveDimensionArray<BorderClassSize>,
	borderSize?: DescriptiveDimensionArray<BorderClassSize>,
	borderColor?: CorvalColor,
	outlineSize?: OutlineClassSize,
	outlineColor?: CorvalColor,
	theme?: CorvalColorTheme,
	margin?: DescriptiveDimensionArray<MarginSize>,
	padding?: DescriptiveDimensionArray<PaddingSize>,
	shadowed?: boolean,
	fill?: 'area' | 'screen',
	rounded?: boolean | BorderClassSides,
	position?: Position,
	top?: string | number | boolean,
	left?: string | number | boolean,
	right?: string | number | boolean,
	bottom?: string | number | boolean,
	zIndex?: ZIndex,
	fadeIn?: boolean,
	
	className?: ClassValue,
	as?: AsTagProp
	
	[ additionalProps: string ]: any
}

const defaultProps: StyledContainerProps = {
	textAlign:    Align.LEFT,
	textColor:    null,
	bgColor:      null,
	border:       null,
	borderSize:   null,
	borderColor:  null,
	outlineSize:  null,
	outlineColor: null,
	theme:        null,
	margin:       null,
	padding:      null,
	shadowed:     null,
	fill:         null,
	rounded:      false,
	position:     null,
	top:          false,
	left:         false,
	right:        false,
	bottom:       false,
	zIndex:       null,
	fadeIn:       false,
	
	className: null,
	as:        'div'
};

const StyledContainer: FC<StyledContainerProps> = React.forwardRef( ( props, ref ) => {
	
	let { children, className, ...p } = props;
	
	let containerClasses = classNames( {
			[ `text-${ p.textAlign }` ]: p.textAlign,
			
			[ `margin-${ p.margin }` ]:       _.isString( p.margin ),
			[ `padding-${ p.padding }` ]:     _.isString( p.padding ),
			[ `border-${ p.borderSize }` ]:   _.isString( p.borderSize ),
			[ `border-${ p.border }` ]:       _.isString( p.border ),
			[ `outline-${ p.outlineSize }` ]: p.outlineSize,
			
			[ `border--colored-${ p.borderColor }` ]:   p.borderColor,
			[ `outline--colored-${ p.outlineColor }` ]: p.outlineColor,
			[ `text--colored-${ p.textColor }` ]:       p.textColor,
			[ `bg--colored-${ p.bgColor }` ]:           p.bgColor,
			
			[ `container--themed-${ p.theme }` ]:    p.theme,
			[ `container--shadowed` ]:               p.shadowed,
			[ `container--fill-${ p.fill }` ]:       p.fill,
			[ `container--rounded` ]:                _.isBoolean( p.rounded ) && p.rounded,
			[ `container--rounded-${ p.rounded }` ]: _.isString( p.rounded ),
			
			[ `position--${ p.position }` ]: p.position,
			[ `position--top` ]:             _.isBoolean( p.top ) ? p.top : false,
			[ `position--left` ]:            _.isBoolean( p.left ) ? p.left : false,
			[ `position--right` ]:           _.isBoolean( p.right ) ? p.right : false,
			[ `position--bottom` ]:          _.isBoolean( p.bottom ) ? p.bottom : false,
			[ `z-index--${ p.zIndex }` ]:    p.zIndex,
			[ `fade-in` ]:                   p.fadeIn,
		},
		
		( _.isArray( p.margin ) && p.margin.length === 2 ) ? {
			[ `margin-${ p.margin[ 0 ] }-vertical` ]:   p.margin[ 0 ],
			[ `margin-${ p.margin[ 1 ] }-horizontal` ]: p.margin[ 1 ],
		} : {},
		
		( _.isArray( p.margin ) && p.margin.length === 4 ) ? {
			[ `margin-${ p.margin[ 0 ] }-top` ]:    p.margin[ 0 ],
			[ `margin-${ p.margin[ 1 ] }-right` ]:  p.margin[ 1 ],
			[ `margin-${ p.margin[ 2 ] }-bottom` ]: p.margin[ 2 ],
			[ `margin-${ p.margin[ 3 ] }-left` ]:   p.margin[ 3 ],
		} : {},
		
		( _.isArray( p.padding ) && p.padding.length === 2 ) ? {
			[ `padding-${ p.padding[ 0 ] }-vertical` ]:   p.padding[ 0 ],
			[ `padding-${ p.padding[ 1 ] }-horizontal` ]: p.padding[ 1 ],
		} : {},
		
		( _.isArray( p.padding ) && p.padding.length === 4 ) ? {
			[ `padding-${ p.padding[ 0 ] }-top` ]:    p.padding[ 0 ],
			[ `padding-${ p.padding[ 1 ] }-right` ]:  p.padding[ 1 ],
			[ `padding-${ p.padding[ 2 ] }-bottom` ]: p.padding[ 2 ],
			[ `padding-${ p.padding[ 3 ] }-left` ]:   p.padding[ 3 ],
		} : {},
		
		( _.isArray( p.border ) && p.border.length === 2 ) ? {
			[ `border-${ p.border[ 0 ] }-vertical` ]:   p.border[ 0 ],
			[ `border-${ p.border[ 1 ] }-horizontal` ]: p.border[ 1 ],
		} : {},
		
		( _.isArray( p.border ) && p.border.length === 4 ) ? {
			[ `border-${ p.border[ 0 ] }-top` ]:    p.border[ 0 ],
			[ `border-${ p.border[ 1 ] }-right` ]:  p.border[ 1 ],
			[ `border-${ p.border[ 2 ] }-bottom` ]: p.border[ 2 ],
			[ `border-${ p.border[ 3 ] }-left` ]:   p.border[ 3 ],
		} : {},
		
		className,
	);
	
	let Tag = props.as;
	
	/* Add positional style is needed */
	let positionalStyle = _.omit( {
		top:    !_.isBoolean( p.top ) ? p.top : null,
		left:   !_.isBoolean( p.left ) ? p.left : null,
		right:  !_.isBoolean( p.right ) ? p.right : null,
		bottom: !_.isBoolean( p.bottom ) ? p.bottom : null,
	}, v => _.isNull( v ) );
	
	let styles = { ...positionalStyle, ...p.style };
	
	return (
		<Tag ref={ ref } className={ containerClasses } style={ styles } { ..._.omit( p, [ ..._.keys( defaultProps ), 'ref', 'style' ] ) }>
			{ children }
		</Tag>
	);
} );

StyledContainer.defaultProps = defaultProps;

export { StyledContainer }
